import type {
  MessagePartStatus,
  MessagePartStreamStatus,
  ThreadAssistantMessagePart,
  ThreadMessage,
  ThreadUserMessagePart,
  ToolCallMessagePart,
  ToolCallMessagePartStatus,
} from "../types/message";

export const COMPLETE_STATUS: MessagePartStatus = Object.freeze({
  type: "complete",
});
export const RUNNING_STATUS: MessagePartStatus = Object.freeze({
  type: "running",
});
type IncompleteMessagePartStatus = Extract<
  MessagePartStreamStatus,
  { readonly type: "incomplete" }
>;
const INCOMPLETE_STATUSES: Readonly<
  Record<IncompleteMessagePartStatus["reason"], IncompleteMessagePartStatus>
> = Object.freeze({
  cancelled: Object.freeze({ type: "incomplete", reason: "cancelled" }),
  length: Object.freeze({ type: "incomplete", reason: "length" }),
  "content-filter": Object.freeze({
    type: "incomplete",
    reason: "content-filter",
  }),
  other: Object.freeze({ type: "incomplete", reason: "other" }),
  error: Object.freeze({ type: "incomplete", reason: "error" }),
});

export const normalizePartStatus = (
  part: ThreadUserMessagePart | ThreadAssistantMessagePart,
): MessagePartStatus | undefined => {
  const status = (part as { readonly status?: unknown }).status;
  if (!status || typeof status !== "object") return undefined;

  const { type } = status as { readonly type?: unknown };
  if (type === "running") return RUNNING_STATUS;
  if (type === "complete") return COMPLETE_STATUS;
  if (type !== "incomplete") return undefined;

  const { reason } = status as {
    readonly reason?: unknown;
  };
  const normalizedReason: IncompleteMessagePartStatus["reason"] =
    reason === "cancelled" ||
    reason === "length" ||
    reason === "content-filter" ||
    reason === "other" ||
    reason === "error"
      ? reason
      : "other";

  return INCOMPLETE_STATUSES[normalizedReason];
};

export const hasPendingToolAction = (
  part: Pick<ToolCallMessagePart, "approval" | "interrupt">,
): boolean =>
  part.interrupt != null ||
  (part.approval != null &&
    part.approval.approved === undefined &&
    part.approval.resolution === undefined);

export const toMessagePartStatus = (
  message: ThreadMessage,
  partIndex: number,
  part: ThreadUserMessagePart | ThreadAssistantMessagePart,
): ToolCallMessagePartStatus => {
  if (message.role !== "assistant") return COMPLETE_STATUS;

  if (part.type === "tool-call") {
    if (
      part.result === undefined ||
      part.isPreliminary ||
      hasPendingToolAction(part)
    ) {
      return message.status;
    } else {
      return COMPLETE_STATUS;
    }
  }

  if (message.status.type === "running") {
    const status = normalizePartStatus(part);
    if (status) return status;
  }

  const isLastPart = partIndex === Math.max(0, message.content.length - 1);
  if (message.status.type === "requires-action") return COMPLETE_STATUS;
  return isLastPart ? (message.status as MessagePartStatus) : COMPLETE_STATUS;
};
